import type { Testimonial } from "~/data/testimonials";

const SITE_URL = "https://georgiadream.ge";

// Map ISO country code → name for `author.address.addressCountry`. Only the
// countries currently represented in `data/testimonials.ts` need entries; new
// reviews from other countries should add their code here.
const COUNTRY_NAMES: Record<string, string> = {
  GB: "United Kingdom",
  CH: "Switzerland",
  KZ: "Kazakhstan",
  IL: "Israel",
  GE: "Georgia",
  EE: "Estonia",
  US: "United States",
};

export interface ReviewLdInput {
  testimonials: readonly Testimonial[];
  itemReviewedId?: string;
}

/**
 * Returns one Schema.org Review node per testimonial, ready to drop into the
 * homepage `@graph`. Uses the original-language quote (`originalLanguage`),
 * not the EN translation, so each Review reflects what the guest actually
 * wrote on Airbnb.
 */
export function buildReviewLdNodes({
  testimonials,
  itemReviewedId = `${SITE_URL}/#lodgingbusiness`,
}: ReviewLdInput) {
  return testimonials.map((t) => {
    const lang = t.originalLanguage;
    const cityCountry = t.location.split(",").map((s) => s.trim());
    const addressLocality = cityCountry[0];
    const country = COUNTRY_NAMES[t.countryCode] || t.countryCode;
    const datePublished = `${t.date}-01`;

    return {
      "@type": "Review",
      "@id": `${SITE_URL}/#review-${t.id}`,
      itemReviewed: { "@id": itemReviewedId },
      author: {
        "@type": "Person",
        name: t.firstName,
        // No `url` here: every testimonial shares the host's Airbnb profile URL
        // (data/testimonials.ts → AIRBNB_PROFILE_URL), so emitting it as each
        // reviewer's author.url falsely maps 7 distinct people to one profile —
        // a review-authenticity contradiction Google can penalize. Third-party
        // attribution is carried by `publisher: { Organization "Airbnb" }` below.
        ...(addressLocality
          ? {
              address: {
                "@type": "PostalAddress",
                addressLocality,
                addressCountry: country,
              },
            }
          : {}),
      },
      datePublished,
      reviewRating: {
        "@type": "Rating",
        ratingValue: 5,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody: t.quote[lang],
      inLanguage: lang,
      publisher: {
        "@type": "Organization",
        name: "Airbnb",
        url: "https://www.airbnb.com",
      },
    };
  });
}
